import { useState } from 'react';
import { Flex, Typography } from 'antd';
import {
  FacilitySelect,
  type FacilitySelectValue,
} from '@admin-panel/components/lib/select/facility/facilityselect';
import { VendorSelect } from '@admin-panel/components/lib/select/vendor/vendorselect';
import { FacilityType, IERPUser, IFacility, IVendor } from '@admin-panel/types';
import { useOptionalVendorGlobalContext } from '@admin-panel/hooks';

type VendorContextShape = {
  vendor?: IVendor;
  setVendor: (vendor: IVendor | undefined) => void;
  vendorFacility?: IFacility;
  setVendorFacility: (vendorFacility: IFacility | undefined) => void;
};

const isSuperAdmin = (user?: IERPUser) =>
  Boolean(user?.roles?.some(role => role.name === 'SUPER_ADMIN'));

const isNexusSuperAdmin = (user?: IERPUser) =>
  Boolean(user?.roles?.some(role => role.name === 'NEXUS_SUPER_ADMIN'));

const VendorSelectorInner = ({
  user,
  vendorContext,
}: {
  user?: IERPUser;
  vendorContext: VendorContextShape;
}) => {
  const { vendor, setVendor, vendorFacility, setVendorFacility } = vendorContext;
  const [pendingVendor, setPendingVendor] = useState<IVendor | undefined>(vendor);
  const [pendingFacility, setPendingFacility] = useState<IFacility | undefined>(vendorFacility);
  const displayVendor = pendingVendor ?? vendor;
  const displayFacility = pendingFacility ?? vendorFacility;

  const handleVendorFacilitySelection = (selectedValue: FacilitySelectValue) => {
    const nextValue = Array.isArray(selectedValue)
      ? selectedValue[selectedValue.length - 1]
      : selectedValue;

    if (nextValue === undefined) {
      setVendorFacility(undefined);
      return;
    }
  };

  const handleVendorSelectionChange = (selectedValue: number | number[] | undefined | null) => {
    if (Array.isArray(selectedValue)) {
      selectedValue = selectedValue[0];
    }

    if (!selectedValue) {
      setPendingVendor(undefined);
      setPendingFacility(undefined);
      return;
    }

    setPendingFacility(undefined);
  };

  if (isNexusSuperAdmin(user)) {
    const handlePendingVendor = (nextVendor: IVendor | undefined) => {
      setPendingVendor(nextVendor);
      setPendingFacility(undefined);
    };

    const handlePendingFacilitySelection = (selectedValue: FacilitySelectValue) => {
      const nextValue = Array.isArray(selectedValue)
        ? selectedValue[selectedValue.length - 1]
        : selectedValue;

      if (nextValue === undefined) {
        setPendingFacility(undefined);
      }
    };

    const handlePendingFacility = (facility?: IFacility) => {
      setPendingFacility(facility);

      const activeVendor = pendingVendor ?? vendor;
      if (!activeVendor || !facility) {
        return;
      }

      setVendor(activeVendor);
      setVendorFacility(facility);
    };

    return (
      <Flex vertical={false} justify="flex-start" gap={8} align="center">
        <VendorSelect
          status={displayVendor === undefined ? 'error' : ''}
          value={displayVendor?.id}
          style={{
            width: 320,
          }}
          showSearch={false}
          setVendor={handlePendingVendor}
          onSelect={handleVendorSelectionChange}
          selectedVendor={displayVendor}
          onChange={handleVendorSelectionChange}
          allowClear
        />
        {displayVendor && (
          <FacilitySelect
            key={`facility-${displayVendor.id}`}
            status={displayFacility?.id === undefined ? 'error' : ''}
            value={displayFacility?.id}
            style={{
              width: 250,
            }}
            showSearch={false}
            filters={[
              {
                field: 'vendorId',
                operator: 'eq',
                value: displayVendor.id,
              },
              {
                field: 'facilityType',
                operator: 'eq',
                value: FacilityType.VENDOR,
              },
            ]}
            onChange={handlePendingFacilitySelection}
            onSelect={handlePendingFacilitySelection}
            setFacility={handlePendingFacility}
          />
        )}
      </Flex>
    );
  }

  if (user?.userType === 'internal' && isSuperAdmin(user)) {
    return (
      <VendorSelect
        status={vendor === undefined ? 'error' : ''}
        value={vendor?.id}
        style={{
          width: 400,
        }}
        showSearch={false}
        setVendor={setVendor}
        selectedVendor={vendor}
      />
    );
  }

  if (user?.userType === 'vendor' && vendor) {
    return (
      <Flex vertical={false} justify="flex-start" gap={4} align="center">
        <Typography.Text
          style={{
            fontWeight: 'bold',
            fontSize: 16,
            textAlign: 'center',
          }}
        >
          {vendor.name}
        </Typography.Text>
        <FacilitySelect
          status={vendorFacility?.id == undefined ? 'error' : ''}
          value={vendorFacility?.id}
          style={{
            width: 250,
          }}
          showSearch={false}
          filters={[
            {
              field: 'vendorId',
              operator: 'eq',
              value: vendor.id,
            },
            {
              field: 'facilityType',
              operator: 'eq',
              value: FacilityType.VENDOR,
            },
          ]}
          onChange={handleVendorFacilitySelection}
          onSelect={handleVendorFacilitySelection}
          setFacility={setVendorFacility}
        />
      </Flex>
    );
  }

  return null;
};

export const VendorSelector = ({ user }: { user?: IERPUser }) => {
  const vendorContext = useOptionalVendorGlobalContext();

  if (!vendorContext) {
    return null;
  }

  const contextKey = `${vendorContext.vendor?.id ?? 'none'}-${
    vendorContext.vendorFacility?.id ?? 'none'
  }`;

  return <VendorSelectorInner key={contextKey} user={user} vendorContext={vendorContext} />;
};
